/* Let Me Be: shared helpers and the generic overlay element. Loaded with defer on every page. */
(() => {
  const LMB = (window.LMB = window.LMB || {});

  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  LMB.focusables = (root) =>
    Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => el.getClientRects().length > 0);

  LMB.fetchHTML = async (url, options = {}) => {
    const response = await fetch(url, { credentials: 'same-origin', ...options });
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    const text = await response.text();
    return new DOMParser().parseFromString(text, 'text/html');
  };

  /* Updates every bag count on the page (header badge, menu drawer) and the bag link's accessible name. */
  LMB.updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count], [data-cart-count-text]').forEach((el) => {
      el.textContent = count;
    });
    document.querySelectorAll('[data-cart-open][data-label-one]').forEach((link) => {
      const template = count === 1 ? link.dataset.labelOne : link.dataset.labelOther;
      if (template) link.setAttribute('aria-label', template.replace('[count]', count));
    });
  };

  /* Scroll lock that tolerates nested overlays. */
  let lockCount = 0;
  LMB.lockScroll = () => {
    if (lockCount++ === 0) document.documentElement.classList.add('is-locked');
  };
  LMB.unlockScroll = () => {
    if (lockCount > 0 && --lockCount === 0) document.documentElement.classList.remove('is-locked');
  };

  /**
   * <lmb-overlay id="…" hidden> with a .overlay__scrim and a .overlay__panel[role=dialog].
   * Opened by any element with data-overlay-open="<id>", closed by [data-overlay-close], Esc or the scrim.
   * Only one overlay is open at a time.
   */
  class LmbOverlay extends HTMLElement {
    connectedCallback() {
      this.onKeydown = this.onKeydown.bind(this);
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-overlay-close]')) {
          event.preventDefault();
          this.close();
        }
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(trigger) {
      if (this.isOpen) return;
      document.dispatchEvent(new CustomEvent('lmb:overlay-open', { detail: { overlay: this } }));
      this.trigger = trigger || document.activeElement;
      this.hidden = false;
      // Force a reflow so the opening transition runs.
      void this.offsetWidth;
      this.classList.add('is-open');
      this.setExpanded(true);
      if (!this.hasAttribute('data-no-lock')) LMB.lockScroll();
      document.addEventListener('keydown', this.onKeydown);
      const target = this.querySelector('[autofocus]') || LMB.focusables(this)[0];
      if (target) requestAnimationFrame(() => target.focus({ preventScroll: true }));
      this.dispatchEvent(new CustomEvent('lmb:open', { bubbles: true }));
    }

    close({ restoreFocus = true } = {}) {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setExpanded(false);
      if (!this.hasAttribute('data-no-lock')) LMB.unlockScroll();
      document.removeEventListener('keydown', this.onKeydown);
      const panel = this.querySelector('.overlay__panel');
      const finish = () => {
        if (!this.isOpen) this.hidden = true;
      };
      const duration = panel ? parseFloat(getComputedStyle(panel).transitionDuration) : 0;
      if (duration > 0) {
        panel.addEventListener('transitionend', finish, { once: true });
        setTimeout(finish, duration * 1000 + 50);
      } else {
        finish();
      }
      if (restoreFocus && this.trigger && document.contains(this.trigger)) this.trigger.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('lmb:close', { bubbles: true }));
    }

    setExpanded(value) {
      document
        .querySelectorAll(`[aria-controls="${this.id}"]`)
        .forEach((el) => el.setAttribute('aria-expanded', String(value)));
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = LMB.focusables(this);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('lmb-overlay')) customElements.define('lmb-overlay', LmbOverlay);

  document.addEventListener('lmb:overlay-open', (event) => {
    document.querySelectorAll('lmb-overlay.is-open').forEach((overlay) => {
      if (overlay !== event.detail.overlay) overlay.close({ restoreFocus: false });
    });
  });

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-overlay-open]');
    if (!opener) return;
    const overlay = document.getElementById(opener.dataset.overlayOpen);
    if (!overlay || typeof overlay.open !== 'function') return;
    event.preventDefault();
    overlay.isOpen ? overlay.close() : overlay.open(opener);
  });

  /* Keeps the header's position on :root, so other parts can sit exactly under it:
     --header-top / --header-bottom: px from the top of the viewport (search, the cart page's summary, scroll margins);
     --header-height: the height that stays pinned at the top while scrolling (bar + chips), 0 when the header
     isn't sticky (the collection's Filter · Sort row pins under it).
     A value is only written when it changes: every write on :root restyles the page.
     Returns a function that stops tracking (the theme editor replaces the header on each change). */
  LMB.trackHeader = (header, { sticky = false } = {}) => {
    const root = document.documentElement;
    const written = {};
    let frame = null;
    const write = (name, px) => {
      const value = `${px}px`;
      if (written[name] === value) return;
      written[name] = value;
      root.style.setProperty(name, value);
    };
    const update = () => {
      frame = null;
      if (!header.isConnected) return;
      const rect = header.getBoundingClientRect();
      write('--header-top', Math.max(0, rect.top));
      write('--header-bottom', Math.max(0, rect.bottom));
      write('--header-height', sticky ? rect.height : 0);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const observer = new ResizeObserver(schedule);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    observer.observe(header);
    update();
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
      // Header hidden or removed in the theme editor: clear the values so what sits under it falls back to its
      // defaults. A re-rendered header is already in the page by now and has written its own values: left alone.
      queueMicrotask(() => {
        if (document.querySelector('lmb-header')) return;
        ['--header-top', '--header-bottom', '--header-height'].forEach((name) => root.style.removeProperty(name));
      });
    };
  };
})();
